import { useState } from "react";
import { pageRouteUrls } from "../helper/pageRouteUrls";
import {
    Badge, Box, Button, Chip, Divider, IconButton,
    ListItem, Popover, Tooltip, Typography,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PaymentIcon from "@mui/icons-material/Payment";
import { useShoppingUtility } from "../contexts/useShoppingUtility";
import PriceComponent from "../component/priceComponent";
import { useCurrUserUtility } from "../contexts/useCurrUserUtility";
import { UserMenu } from "./userMenu";
import { NotificationMenu } from "../component/notificationMenu";
import { getDefaultImage } from "../helper/functions";

// ── Totals grouped by currency ────────────────────────────────────────────────
interface CurrencyTotal { total: number; currencyCode: string }
const calcTotals = (items: ReturnType<ReturnType<typeof useShoppingUtility>["getLectures"]>) =>
    items.reduce((acc: Record<string, CurrencyTotal>, item) => {
        if (!acc[item.currencyCode])
            acc[item.currencyCode] = { total: 0, currencyCode: item.currencyCode };
        acc[item.currencyCode].total += item.price;
        return acc;
    }, {});

// ─────────────────────────────────────────────────────────────────────────────

const UserNavIcon = () => {
    const { cartItems, getLectures, removeLecture } = useShoppingUtility();
    const { isAuth } = useCurrUserUtility();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);

    const items = getLectures();
    const count = cartItems.length;
    const totals = calcTotals(items);

    return (
        <>
            {/* ── Cart icon with badge ──────────────────────────────────── */}
            <li style={{ marginInlineStart: "5px" }}>
                <Tooltip title={count > 0 ? `${count} item${count > 1 ? "s" : ""} in cart` : "Cart is empty"}>
                    <IconButton onClick={e => setAnchor(e.currentTarget)} size="small"
                        sx={{ color: "inherit" }}>
                        <Badge badgeContent={count} color="primary"
                            sx={{ "& .MuiBadge-badge": { fontWeight: 700, fontSize: 11 } }}>
                            {count > 0
                                ? <ShoppingCartIcon sx={{ fontSize: 22 }} />
                                : <ShoppingCartOutlinedIcon sx={{ fontSize: 22 }} />}
                        </Badge>
                    </IconButton>
                </Tooltip>

                {/* ── Cart dropdown ─────────────────────────────────────── */}
                <Popover
                    open={Boolean(anchor)}
                    anchorEl={anchor}
                    onClose={() => setAnchor(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    slotProps={{
                        paper: {
                            elevation: 8,
                            sx: { width: 360, borderRadius: 3, mt: 1, overflow: "hidden" },
                        },
                    }}
                >
                    {/* Header */}
                    <Box sx={{
                        background: "linear-gradient(135deg,#309255 0%,#1e6040 100%)",
                        px: 2, py: 1.5,
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                    }}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <ShoppingCartIcon sx={{ color: "#fff", fontSize: 18 }} />
                            <Typography fontWeight={800} fontSize={15} color="#fff">Your Cart</Typography>
                        </Box>
                        <Chip
                            label={`${count} item${count !== 1 ? "s" : ""}`}
                            size="small"
                            sx={{ bgcolor: "rgba(255,255,255,0.25)", color: "#fff", fontWeight: 700, fontSize: 11, height: 20 }}
                        />
                    </Box>

                    {/* Item list */}
                    {count === 0 ? (
                        /* Empty state */
                        <Box py={5} textAlign="center">
                            <ShoppingCartOutlinedIcon sx={{ fontSize: 48, color: "#ddd", mb: 1 }} />
                            <Typography fontSize={14} color="text.secondary">Your cart is empty</Typography>
                        </Box>
                    ) : (
                        <>
                            <Box sx={{ maxHeight: 320, overflowY: "auto" }}>
                                {items.map((item, idx) => (
                                    <Box key={item.token}>
                                        <Box display="flex" alignItems="center" gap={1.5} px={2} py={1.5}>
                                            {/* Thumbnail */}
                                            <Box flexShrink={0} sx={{
                                                width: 64, height: 48, borderRadius: 1.5,
                                                overflow: "hidden", border: "1px solid #eee",
                                            }}>
                                                <a href={pageRouteUrls.LectureView(item.token)}
                                                    onClick={() => setAnchor(null)}>
                                                    <img
                                                        src={item.imageUrl || getDefaultImage(item.title, 64)}
                                                        alt={item.title}
                                                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                                    />
                                                </a>
                                            </Box>

                                            {/* Info */}
                                            <Box flex={1} minWidth={0}>
                                                <Typography
                                                    component="a"
                                                    href={pageRouteUrls.LectureView(item.token)}
                                                    onClick={() => setAnchor(null)}
                                                    fontSize={13}
                                                    fontWeight={600}
                                                    sx={{
                                                        display: "block",
                                                        color: "inherit",
                                                        textDecoration: "none",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                        "&:hover": { color: "#309255" },
                                                    }}
                                                >
                                                    {item.title}
                                                </Typography>
                                                <Typography fontSize={12} fontWeight={700} color="#309255" mt={0.25}>
                                                    <PriceComponent price={item.price} currency={item.currencyCode} />
                                                </Typography>
                                            </Box>

                                            {/* Remove */}
                                            <Tooltip title="Remove">
                                                <IconButton size="small"
                                                    onClick={() => removeLecture(item.token)}
                                                    sx={{ color: "#ccc", flexShrink: 0, "&:hover": { color: "#e53935", bgcolor: "#fdecea" } }}>
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                        {idx < items.length - 1 && <Divider />}
                                    </Box>
                                ))}
                            </Box>

                            {/* Footer: totals + checkout */}
                            <Box sx={{ borderTop: "1px solid #eee", px: 2, py: 1.5 }}>
                                {Object.entries(totals).map(([currency, data]) => (
                                    <Box key={currency} display="flex" justifyContent="space-between" alignItems="center" mb={0.25}>
                                        <Typography fontSize={13} color="text.secondary" fontWeight={600}>
                                            Total ({currency})
                                        </Typography>
                                        <Typography fontSize={15} fontWeight={800} color="#309255">
                                            <PriceComponent price={data.total} currency={data.currencyCode} />
                                        </Typography>
                                    </Box>
                                ))}

                                <Button
                                    fullWidth
                                    href={pageRouteUrls.Checkout()}
                                    onClick={() => setAnchor(null)}
                                    startIcon={<PaymentIcon />}
                                    sx={{
                                        mt: 1.5,
                                        py: 1.1,
                                        borderRadius: 2,
                                        fontWeight: 800,
                                        fontSize: 14,
                                        textTransform: "none",
                                        background: "linear-gradient(135deg,#309255 0%,#1e6040 100%)",
                                        color: "#fff",
                                        boxShadow: "0 3px 10px rgba(48,146,85,0.3)",
                                        "&:hover": {
                                            background: "linear-gradient(135deg,#267a46 0%,#174f31 100%)",
                                            boxShadow: "0 5px 14px rgba(48,146,85,0.4)",
                                        },
                                    }}
                                >
                                    Proceed to Checkout
                                </Button>
                            </Box>
                        </>
                    )}
                </Popover>
            </li>

            {/* ── User / Notification icons ─────────────────────────────── */}
            {isAuth() ? (
                <>
                    <ListItem>
                        <NotificationMenu />
                    </ListItem>
                    <ListItem className="usrbtnMenu">
                        <UserMenu />
                    </ListItem>
                </>
            ) : (
                <>
                    <li><a className="sign-in" href={pageRouteUrls.Login()}>Sign In</a></li>
                    <li><a className="sign-up" href={pageRouteUrls.Register()}>Sign Up</a></li>
                </>
            )}
        </>
    );
};

export default UserNavIcon;
